import { memo } from "react";
import DataContainer from "../../services/DataContainer";
import { Typography } from "../../components/typography";
import useWindowSize from "../../utils/useWindowSize";

type Props = {
  currentTruckIndex: number;
  totalTrucks: number;
  isSequencesLoading: boolean;
  isTransitioning: boolean;
  currentTruckDisplayName: string;
  currentTruckName: string;
  goToPrevious: () => void;
  goToNext: () => void;
};

export const TruckInfoPanel = memo(
  ({
    currentTruckIndex,
    totalTrucks,
    isSequencesLoading,
    isTransitioning,
    currentTruckDisplayName,
    currentTruckName,
    goToPrevious,
    goToNext,
  }: Props) => {
    const { windowHeight } = useWindowSize();
    const isMiniRes = windowHeight <= 650;

    return (
      <div
        className={`flex flex-col items-center w-full xxl:w-[565px] px-10 xxl:px-0 ${
          isMiniRes ? "gap-0" : "gap-3 lg:gap-2 xxl:gap-4"
        }`}
      >
        <div
          className={`flex flex-col items-center justify-center ${
            isMiniRes ? "gap-1" : "gap-4"
          }`}
        >
          <div className="text-sm text-[#53565A]">
            {currentTruckIndex + 1}/{totalTrucks}
          </div>
        </div>
        <div className="flex flex-row items-center justify-between w-full px-2 lg:px-24 xxl:px-0">
          <NavigationButton
            direction="prev"
            onClick={goToPrevious}
            disabled={
              isSequencesLoading ||
              isTransitioning ||
              totalTrucks < 2 ||
              (totalTrucks === 2 && currentTruckIndex === 0)
            }
            dimIcon={
              isSequencesLoading ||
              totalTrucks < 2 ||
              (totalTrucks === 2 && currentTruckIndex === 0)
            }
          />

          <div
            className={`flex items-center justify-center px-2 text-center ${
              isMiniRes ? "h-[44px]" : "h-[44px] md:h-[50px]"
            }`}
          >
            <Typography
              tag="h1"
              className={`text-center font-medium leading-tight line-clamp-2 ${
                isMiniRes
                  ? "text-3xl md:text-3xl3"
                  : "text-2xl1 sm:text-3xl md:text-3xl3 2md:text-4xl"
              }`}
            >
              {currentTruckDisplayName}
            </Typography>
          </div>

          <NavigationButton
            direction="next"
            onClick={goToNext}
            disabled={
              isSequencesLoading ||
              isTransitioning ||
              totalTrucks < 2 ||
              (totalTrucks === 2 && currentTruckIndex === 1)
            }
            dimIcon={
              isSequencesLoading ||
              totalTrucks < 2 ||
              (totalTrucks === 2 && currentTruckIndex === 1)
            }
          />
        </div>

        <div
          className={`flex items-start justify-center ${
            isMiniRes
              ? "h-[80px] xs:h-[55px] md:h-[110px] 2md:h-[70px]"
              : "h-[125px] xs:h-[100px] md:h-[95px]"
          }`}
        >
          <p
            className={`text-gray-700 text-center ${
              isMiniRes ? "line-clamp-5" : "line-clamp-7"
            } leading-tight ${
              isMiniRes
                ? "text-[0.75rem] md:text-[0.875rem] lg:text-base"
                : "text-sm md:text-base"
            }`}
          >
            {DataContainer.sectionKey(
              "TruckSelector",
              `${currentTruckName}_description_body`,
            )}
          </p>
        </div>

        {/* {isDesktop && (
        <div className={`flex justify-center ${isMiniRes ? "gap-3 md:gap-5 lg:gap-10" : "gap-5 md:gap-10 lg:gap-20"}`}>
          <div className="text-center flex flex-col">
            <div className="text-sm md:text-base leading-none text-[#53565A] font-normal">
              {DataContainer.sectionKey(
                "TruckSelector",
                `${currentTruckName}_stat1_title`,
              )}
            </div>
            <div className="text-base md:text-lg leading-none whitespace-nowrap font-medium">
              {DataContainer.sectionKey(
                "TruckSelector",
                `${currentTruckName}_stat1_value`,
              )}
            </div>
          </div>
          <div className="text-center flex flex-col">
            <div className="text-sm md:text-base leading-none text-[#53565A] font-normal">
              {DataContainer.sectionKey(
                "TruckSelector",
                `${currentTruckName}_stat2_title`,
              )}
            </div>
            <div className="text-base md:text-lg leading-none whitespace-nowrap font-medium">
              {DataContainer.sectionKey(
                "TruckSelector",
                `${currentTruckName}_stat2_value`,
              )}
            </div>
          </div>
          <div className="text-center flex flex-col">
            <div className="text-sm md:text-base leading-none text-[#53565A] font-normal">
              {DataContainer.sectionKey(
                "TruckSelector",
                `${currentTruckName}_stat3_title`,
              )}
            </div>
            <div className="text-base md:text-lg leading-none whitespace-nowrap font-medium">
              {DataContainer.sectionKey(
                "TruckSelector",
                `${currentTruckName}_stat3_value`,
              )}
            </div>
          </div>
        </div>
      )} */}
      </div>
    );
  },
);

TruckInfoPanel.displayName = "TruckInfoPanel";

type NavigationButtonProps = {
  direction: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
  dimIcon: boolean;
};

const NavigationButton = ({
  direction,
  onClick,
  disabled,
  dimIcon,
}: NavigationButtonProps) => {
  const isPrev = direction === "prev";
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center ${
        !isPrev ? "transition-colors" : ""
      }`}
      aria-label={isPrev ? "Previous truck" : "Next truck"}
      disabled={disabled}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
      >
        {isPrev ? (
          <rect
            x="32"
            y="32"
            width="32"
            height="32"
            rx="16"
            transform="rotate(-180 32 32)"
            fill="#F5F5F5"
          />
        ) : (
          <rect width="32" height="32" rx="16" fill="#F5F5F5" />
        )}
        <path
          d={isPrev ? "M18 20L14 16L18 12" : "M14 12L18 16L14 20"}
          stroke="black"
          opacity={dimIcon ? "0.3" : "1"}
        />
      </svg>
    </button>
  );
};
